import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  AlertCircleIcon,
  CircleCheckIcon,
  LogOutIcon,
  MoonIcon,
  RotateCcwIcon,
  SearchIcon,
  SendHorizonalIcon,
  SunIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';
import { useTheme } from '../../../../services/state/theme-state.jsx';
import {
  CAMPAIGN_TERMS_OF_DATA_SHARING,
  CAMPAIGN_TERMS_OF_DATA_SHARING_LIVLI_FAMILY,
  CAMPAIGN_TERMS_OF_PARTICIPATION,
} from '../../../../utils/constants.js';

import DecorativeInput from '../../../decorative-input.jsx';
import Error from '../../../feedback/error.jsx';
import Loading from '../../../feedback/loading.jsx';
import { Button } from '../../../ui/button.jsx';
import { Checkbox } from '../../../ui/checkbox.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form.jsx';

export default function DashboardTablet() {
  const { logout } = useSnapshot(authState);
  const { setTheme, theme } = useTheme();

  const [successfullApproval, setSuccessfullApproval] = useState(false);
  const [errorApproval, setErrorApproval] = useState(false);
  const [action, setAction] = useState('create');
  const [searchCustomerConsentRequest, setSearchCustomerConsentRequest] =
    useState('');
  const [
    debouncedSearchCustomerConsentRequest,
    setDebouncedSearchCustomerConsentRequest,
  ] = useState('');
  const [customerHasEmail, setCustomerHasEmail] = useState(false);

  const formCustomerConsents = useForm({
    mode: 'onChange',
    defaultValues: {
      consentTermsOfParticipation: false,
      consentDataSharing: false,
    },
  });

  const watchIsConsentValid = formCustomerConsents.watch(
    'consentTermsOfParticipation',
  );

  const querySearchCustomerConsentRequest = useQuery({
    queryKey: ['search_consent_request', debouncedSearchCustomerConsentRequest],
    queryFn: async () => {
      const { data: customerConsentRequest } = await msCatalystApi.get(
        `customers/consent-requests/search/${searchCustomerConsentRequest}`,
      );

      if (customerConsentRequest?.data) {
        setAction(customerConsentRequest.data.action);
      }

      return customerConsentRequest.data;
    },
    select: (data) => {
      if (data) {
        return {
          id: data?.ROWID,
          customer: data?.customer ? JSON.parse(data.customer) : null,
          customerId: data?.customerId,
          customerUserId: data?.customerUserId,
        };
      }

      return data;
    },
    enabled: debouncedSearchCustomerConsentRequest !== '',
  });

  const mutationCreateCustomer = useMutation({
    mutationFn: async (data) => {
      let userId = undefined;

      if (data?.email) {
        const { data: createUser } = await msCatalystApi.post(
          '/auth/register/tablet',
          {
            firstName: data.firstName,
            lastName: data.lastName,
            fullName: `${data.firstName} ${data.lastName}`,
            email: data.email,
            type: 'Customer',
          },
        );

        userId = createUser.data.ROWID;

        setCustomerHasEmail(true);
      }

      const createCustomer = await msCatalystApi.post('/customers', {
        firstName: data.firstName,
        lastName: data.lastName,
        fullName: `${data.firstName} ${data.lastName}`,
        email: data?.email || null,
        cc: data.cc,
        phone: data.phone,
        location: data.location,
        consentTermsOfParticipation: data.consentTermsOfParticipation,
        consentDataSharing: data.consentDataSharing,
        consentDataSharingLivliFamily: data.consentDataSharingLivliFamily,
        birthDate: data.birthDate,
        role: !userId ? 'Cliente sem Conta' : 'Cliente',
        createdIn: 'Backoffice',
        userId,
      });

      return createCustomer.data;
    },
  });

  const mutationDeleteCustomerConsentRequest = useMutation({
    mutationFn: async (data) => {
      await msCatalystApi.delete(
        `/customers/consent-requests/${data.customerConsentRequestId}`,
      );

      return true;
    },
  });

  const mutationUpdateCustomer = useMutation({
    mutationFn: async ({ customerId, data }) => {
      const { data: updatedConsumerRes } = await msCatalystApi.put(
        `/customers/${customerId}`,
        {
          ...data,
          fullName: `${data.firstName} ${data.lastName}`,
        },
      );

      return updatedConsumerRes.data;
    },
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchCustomerConsentRequest(searchCustomerConsentRequest);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [searchCustomerConsentRequest]);

  const onFormSubmit = async (data) => {
    try {
      // Check if the customer has accepted the terms of participation
      if (!data.customer.consentTermsOfParticipation) {
        return toast.error(
          'Para participar nas campanhas do MAR SHOPPING é obrigatório aceitar os Condições de participação',
        );
      }

      // Check if the action is to create a new customer
      if (action === 'create') {
        // If the email is provided, create a customer
        await mutationCreateCustomer.mutateAsync({
          ...data.customer,
        });
      } else {
        data.customer.email = data.customer?.email || null;

        // Handling update for existing customers
        if (data.customerUserId && data.customer?.email) {
          await mutationUpdateCustomer.mutateAsync({
            customerId: querySearchCustomerConsentRequest?.data?.customerId,
            data: { ...data.customer },
          });

          setCustomerHasEmail(true);
        } else {
          // Update existing customer
          await mutationUpdateCustomer.mutateAsync({
            customerId: querySearchCustomerConsentRequest?.data?.customerId,
            data: { ...data.customer },
          });
        }
      }

      // Delete the customer consent request after processing
      await mutationDeleteCustomerConsentRequest.mutateAsync({
        customerConsentRequestId: data.customerConsentRequestId,
      });

      // Set approval state to true and reset after a timeout
      setSuccessfullApproval(true);
      setTimeout(() => {
        setSuccessfullApproval(false);
        setCustomerHasEmail(false);
      }, 5000);
    } catch (err) {
      // Handle errors and set error approval state
      setErrorApproval(true);

      setTimeout(() => {
        setErrorApproval(false);
      }, 5000);
    } finally {
      // Reset the search state regardless of success or failure
      setSearchCustomerConsentRequest('');
      setDebouncedSearchCustomerConsentRequest('');
      formCustomerConsents.reset({
        consentTermsOfParticipation: false,
        consentDataSharing: false,
      });
    }
  };

  return (
    <div
      style={{ gridTemplateRows: 'auto 1fr auto' }}
      className="grid min-h-[100svh] w-[100vw] gap-8"
    >
      <Helmet>
        <title>Plataforma de Campanhas - MAR SHOPPING</title>
      </Helmet>

      <div className="p-10">
        <h1 className="text-center text-3xl font-bold">Dados Pessoais</h1>
      </div>

      <div className="mx-auto w-10/12">
        {successfullApproval ? (
          <div className="mx-auto flex h-full w-auto flex-col items-center justify-center gap-y-4 lg:w-[80%]">
            <CircleCheckIcon className="size-20 text-success" />
            <h2 className="text-2xl text-success">
              {action === 'create'
                ? 'A sua conta foi criada com sucesso!'
                : action === 'update'
                  ? 'A sua conta foi atualizada com sucesso!'
                  : 'O seu consentimento foi renovado com sucesso!'}
            </h2>

            {action === 'create' && customerHasEmail ? (
              <div className="pt-2 text-center">
                <p className="pt-2">
                  Por favor verifique o seu email para definir a sua password e
                  conseguir aceder à sua conta!
                </p>
              </div>
            ) : null}
          </div>
        ) : errorApproval ? (
          <div className="mx-auto flex h-full w-auto flex-col items-center justify-center gap-y-4 lg:w-[80%]">
            <TriangleAlertIcon className="size-20 text-destructive" />
            <h2 className="text-2xl text-destructive">Erro ao processar</h2>
          </div>
        ) : (
          <>
            <div
              key="search-wrapper"
              className="mb-6 flex h-10 items-center rounded-md border border-input !bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-75"
            >
              <SearchIcon className="mr-2 size-4" />

              <input
                type="text"
                placeholder="Pesquisar por Nº de Identificação / Email"
                className="ml-2 size-full border-none bg-transparent focus:outline-none"
                value={searchCustomerConsentRequest}
                onChange={(e) => {
                  if (e.target.value === undefined) {
                    setSearchCustomerConsentRequest('');
                    formCustomerConsents.reset({
                      consentTermsOfParticipation: false,
                      consentDataSharing: false,
                    });
                  } else {
                    setSearchCustomerConsentRequest(e.target.value);
                  }
                }}
              />

              <Button
                variant="ghost"
                className="h-auto p-2"
                onClick={() => {
                  setSearchCustomerConsentRequest('');
                  setDebouncedSearchCustomerConsentRequest('');
                }}
                disabled={
                  querySearchCustomerConsentRequest.isLoading ||
                  querySearchCustomerConsentRequest.isRefetching
                }
              >
                <RotateCcwIcon className="size-4" />
              </Button>
            </div>

            <div>
              {querySearchCustomerConsentRequest.isLoading ||
              querySearchCustomerConsentRequest.isRefetching ? (
                <div className="flex items-center justify-center">
                  <Loading />
                </div>
              ) : querySearchCustomerConsentRequest.isError ||
                querySearchCustomerConsentRequest.isRefetchError ? (
                <Error />
              ) : querySearchCustomerConsentRequest.data ===
                undefined ? null : querySearchCustomerConsentRequest.data ===
                null ? (
                <div className="flex w-full flex-col items-center justify-center">
                  <AlertCircleIcon className="size-20" />
                  <h1 className="py-6 text-xl">
                    Não existe nenhum Pedido de Consentimento para o Cliente
                    pesquisado
                  </h1>
                </div>
              ) : (
                <div className="p-0 shadow-sm md:rounded-lg md:border md:border-muted-foreground md:bg-muted md:p-10">
                  <div className="flex flex-col space-y-4">
                    {formCustomerConsents.formState.isSubmitting ? (
                      <div className="mx-auto flex h-full w-auto flex-col items-center justify-center gap-y-4 lg:w-[80%]">
                        <Loading />
                        <p className="text-2xl">
                          Por favor aguarde uns instantes
                        </p>
                      </div>
                    ) : (
                      <div className="flex h-full w-full flex-col items-start justify-center gap-8 lg:items-center lg:gap-12">
                        <div className="grid w-full grid-cols-1 gap-6 p-0 lg:grid-cols-2">
                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.firstName
                            }
                            label="Nome"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.lastName
                            }
                            label="Apelido"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.cc
                            }
                            label="Nº Identificação"
                          />

                          <DecorativeInput
                            value={format(
                              querySearchCustomerConsentRequest.data.customer
                                ?.birthDate,
                              'dd-MM-yyyy',
                            )}
                            label="Data de Nascimento"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.email
                            }
                            label="Email"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.phone
                            }
                            label="Telemóvel"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.location
                            }
                            label="Localidade"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.address
                            }
                            label="Morada"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.district
                            }
                            label="Distrito"
                          />

                          <DecorativeInput
                            value={
                              querySearchCustomerConsentRequest.data.customer
                                ?.zipCode
                            }
                            label="Código de Postal"
                          />
                        </div>

                        <Form {...formCustomerConsents}>
                          <form
                            onSubmit={formCustomerConsents.handleSubmit(
                              async (data) =>
                                onFormSubmit({
                                  customer: {
                                    ...querySearchCustomerConsentRequest.data
                                      .customer,
                                    ...formCustomerConsents.getValues(),
                                  },
                                  customerUserId:
                                    querySearchCustomerConsentRequest.data
                                      .customerUserId,
                                  customerConsentRequestId:
                                    querySearchCustomerConsentRequest.data.id,
                                }),
                            )}
                          >
                            <div className="flex w-full flex-col gap-8">
                              <FormField
                                control={formCustomerConsents.control}
                                name="consentTermsOfParticipation"
                                render={({ field }) => (
                                  <FormItem>
                                    <h3 className="text-lg font-medium">
                                      Condições de Participação{' '}
                                      <span className="text-red-600">*</span>
                                    </h3>

                                    <div className="flex flex-row items-center space-x-2">
                                      <FormControl>
                                        <Checkbox
                                          className="mr-2 size-6"
                                          checked={field.value}
                                          onCheckedChange={(checked) =>
                                            field.onChange(checked)
                                          }
                                        />
                                      </FormControl>
                                      <FormLabel className="ml-4 space-y-0 text-lg font-normal">
                                        {CAMPAIGN_TERMS_OF_PARTICIPATION}
                                      </FormLabel>
                                    </div>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={formCustomerConsents.control}
                                name="consentDataSharing"
                                render={({ field }) => (
                                  <FormItem>
                                    <h3 className="text-lg font-medium">
                                      Comunicações de Marketing
                                    </h3>
                                    <div className="flex flex-row items-center space-x-2">
                                      <FormControl>
                                        <Checkbox
                                          className="mr-2 size-6"
                                          checked={field.value}
                                          onCheckedChange={(checked) =>
                                            field.onChange(checked)
                                          }
                                        />
                                      </FormControl>
                                      <FormLabel className="ml-4 space-y-0 text-lg font-normal">
                                        {CAMPAIGN_TERMS_OF_DATA_SHARING}
                                      </FormLabel>
                                    </div>

                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={formCustomerConsents.control}
                                name="consentDataSharingLivliFamily"
                                render={({ field }) => (
                                  <FormItem>
                                    <h3 className="text-lg font-medium">
                                      Adesão ao Programa de Vantagens Livli
                                      Family
                                    </h3>
                                    <div className="flex flex-row items-center space-x-2">
                                      <FormControl>
                                        <Checkbox
                                          className="mr-2 size-6"
                                          checked={field.value}
                                          onCheckedChange={(checked) =>
                                            field.onChange(checked)
                                          }
                                        />
                                      </FormControl>
                                      <FormLabel className="ml-4 space-y-0 text-lg font-normal">
                                        {
                                          CAMPAIGN_TERMS_OF_DATA_SHARING_LIVLI_FAMILY
                                        }
                                      </FormLabel>
                                    </div>

                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="mt-16 flex w-full flex-row items-center justify-end gap-4">
                              <Button
                                type="submit"
                                variant="portal"
                                className="text-lg"
                                disabled={!watchIsConsentValid}
                              >
                                Submeter
                                <SendHorizonalIcon className="size-6" />
                              </Button>
                            </div>
                          </form>
                        </Form>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* FOOTER */}
      <footer
        className="text-neutral-grey-700 relative mt-auto"
        data-elastic-exclude="true"
      >
        <div className="relative mx-6 mt-20 flex flex-col items-center justify-center xl:container lg:mt-24 lg:px-36 xl:px-28">
          <div className="mx-auto mb-4 max-h-10 w-36">
            <svg
              className="mx-auto max-h-10"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 462 200"
            >
              <title>Logotipo</title>
              <g fill="none">
                <path fill="#C20012" d="M0 0v200h462V0z"></path>
                <path
                  fill="#FCB813"
                  d="M107.42 138.26c-12.22 3.5-23.55.53-33.84-6.58-2.27-1.56-5.6-1.64-8.2-.96-1.44.38-4.43 2.1-2.13 3.68a58.7 58.7 0 0 0 41.5 10.3c13.8-1.68 26.46-8.4 38.3-15.27 5.7-3.32-5.48-5.55-8.47-3.8-8.65 5.03-17.48 9.85-27.16 12.63zm-23.06-25.8c-.35 3-.75 6.03-1.17 9.04-.32 2.3 4.73 2.7 5.98 2.7 1.78 0 5.66-.33 5.98-2.7.4-3 .82-6.03 1.17-9.05.27-2.3-4.7-2.7-5.98-2.7-1.75 0-5.7.33-6 2.7m21.34-2.77c.63 3.44.95 7 .24 10.46-.68 3.26 11.13 4.05 11.96 0 .72-3.46.4-7.02-.24-10.46-.75-4.05-12.6-3.33-11.97 0m25.21 43.92c-10.05 2.43-20.5 3.1-30.8 3.72-15.28.9-30.8 1.27-45.9 4.04 2.15-15.46 3.1-30.98 1.95-46.6-1-13.56-3.75-26.9-7.13-40.05 5.8-.28 11.48-.8 17.1-1.63-.13 2.15-.2 4.3-.23 6.42-.02 3.8 11.94 3.8 11.97 0 .02-2.9.15-5.84.4-8.8 1.9-.45 3.8-.94 5.7-1.5a848.49 848.49 0 0 0 56.16-18.62c2.66 32.4 8.37 64.37 15.34 96.12-8.2 2.26-16.3 4.88-24.56 6.9M85.23 42.3a23.89 23.89 0 0 1 4.54-6.73c2.63-2.62 2.64-2.2 5.2.72 4.42 5.06 6.03 12.55 8.1 19l-17.72 5.83c-2.07.68-4.15 1.34-6.24 2 1.14-7.25 3.08-14.37 6.13-20.84m84.54 106.1c-7.6-34.42-13.76-69.08-16.2-104.28-.3-4.43-9.48-3.13-11.9-2.22a762.1 762.1 0 0 1-27.75 9.8c-2.42-7.8-4.84-16.62-12-20.85-6.54-3.88-15.7-3.2-21.73 1.34-6.27 4.7-8.84 13.56-10.8 20.75-1.18 4.38-2.05 8.9-2.64 13.46a78.92 78.92 0 0 1-11.97 1.68c-2.4.14-4.9.35-7.4.5l-1.7-6.1c-1.34-4.74-14.96-3.52-13.97 0 .55 1.94 1.1 3.9 1.62 5.86-1.98-.3-3.92-.78-5.8-1.48-3.06-1.14-6.8-1.37-9.85 0-2.4 1.08-2.68 3.44 0 4.45a59.23 59.23 0 0 0 17.33 3.45c3.2 12.53 5.75 25.2 6.98 38.1 1.73 17.96.23 36.02-2.57 53.8-.6 3.74 8.82 3.18 10.5 2.7 18.78-5.25 38.8-4.98 58.12-6.24 9.36-.6 18.6-1.7 27.8-3.6 10.3-2.18 20.27-5.78 30.46-8.4.93-.23 3.8-1.2 3.47-2.7"
                ></path>
                <path
                  fill="#FAB900"
                  d="M175.67 41.8c1.06 0 1.8-.64 1.8-1.6 0-1.04-.58-1.6-1.8-1.6h-3.3v3.2h3.3zm2.8 5.5c.02.03.03.08.04.13 0-.05 0-.1-.02-.14zm.13.37a.33.33 0 0 1-.05-.12c0 .04.02.1.04.13zm-6.23.78h-2.04V36.7h5.42c2.2 0 3.77 1.2 3.77 3.3 0 1.16-.46 2.14-1.27 2.72.67.48 1 1.3 1.06 2.43l.02.23c.03.7.03.74.06 1.02.06.62.17 1.05.3 1.2l.7.85h-2.5l-.15-.25a2.52 2.52 0 0 1-.3-1.07l-.05-.7v-.13c-.14-2.07-.42-2.66-1.65-2.62h-3.36v4.77zm2.37 5.03a10.74 10.74 0 1 1 0-21.47 10.74 10.74 0 0 1 0 21.47zm0-1.87a8.86 8.86 0 1 0-8.86-8.86c0 4.9 3.97 8.87 8.87 8.87z"
                ></path>
                <path
                  fill="#FEFEFE"
                  d="M217.53 75.15v35.33h-15.5V48.42h16.17l14.85 23.9 14.85-23.9h16.18v62.06H248.4v-35.6l-14.62 22.2-.86 1.33-.94-1.32zm92.93-6.55-5.87 14.33h11.72l-5.86-14.32zm-17.06 41.9h-16.3L303.72 48h13.82l26.6 62.48H327.5l-5.43-13.3H298.8l-5.43 13.3zm91.4-33.23c5.52 0 8.66-2.7 8.66-7.04v-.16c0-4.65-3.08-7.13-8.9-7.13h-11.63v14.35h11.88zm-11.87 33.23h-15.76V48.42h28.5c7.78 0 14.02 2.2 18.24 6.42 3.6 3.6 5.5 8.6 5.5 14.52 0 9.2-4.4 15.77-12.02 19.2l15 21.92h-18.54l-12.72-19h-8.2v19zm-169.88 33.1c1.35.7 3.2 1.2 4.86 1.2 1.77 0 2.62-.6 2.62-1.56 0-.88-.7-1.45-3.02-2.25-3.97-1.42-6.15-3.62-6.15-6.77 0-4.05 3.44-6.87 8.5-6.87a13 13 0 0 1 5.56 1.1l.53.25-1.42 5.08-.72-.37a8.88 8.88 0 0 0-4-.92c-1.53 0-2.32.6-2.32 1.32 0 .8.64 1.25 3.4 2.28 3.9 1.45 5.77 3.6 5.77 6.8 0 4.2-3.37 7.08-9 7.08-2.26 0-4.76-.58-6.18-1.37l-.45-.26 1.28-5.17.77.4zm30.78 6.06h-6.02v-22.03h6.03v7.95h6.34v-7.95h6.02v22.03h-6.03V141h-6.34zm29.74-10.98c0 3.83 1.72 6.25 4.36 6.25 2.6 0 4.3-2.5 4.3-6.35 0-3.7-1.7-6.25-4.3-6.25-2.64 0-4.36 2.5-4.36 6.35zm14.94-.25c0 7.07-4.27 11.56-10.82 11.56-6.27 0-10.4-4.7-10.4-11.23 0-6.68 4.4-11.5 10.74-11.5 6.4 0 10.5 4.68 10.5 11.16zm17.9-1c2.16 0 3.36-1 3.36-2.68 0-1.5-1.02-2.36-3-2.36-.5 0-.92 0-1.23.04v4.95l.88.02zm-.87 12.23h-6v-21.64l.58-.1c1.67-.28 3.8-.44 6.44-.44 3 0 5.2.58 6.73 1.8a6.64 6.64 0 0 1 2.47 5.32c0 2.23-.73 4.15-2.12 5.4-1.7 1.57-4.2 2.36-7.17 2.36-.34 0-.65 0-.92-.03v7.33zm28.02-12.24c2.15 0 3.34-1 3.34-2.67 0-1.5-1.02-2.36-3-2.36-.5 0-.92 0-1.23.04v4.95l.88.02zm-.88 12.24h-6v-21.64l.58-.1c1.66-.28 3.78-.44 6.43-.44 3 0 5.2.58 6.73 1.8a6.62 6.62 0 0 1 2.48 5.32c0 2.23-.74 4.15-2.12 5.4-1.7 1.57-4.2 2.36-7.18 2.36-.33 0-.64 0-.9-.03v7.33zm21.17 0v-22.02h6.02v22.02zm23.68-6.9v6.9h-5.65v-22.02h6.53l.2.34 4.3 7.57c.63 1.13 1.28 2.4 1.9 3.72-.07-1.5-.1-3.14-.1-4.95v-6.68h5.65v22.02h-5.98l-.2-.35-4.4-7.96a80.58 80.58 0 0 1-2.32-4.43c.05 1.8.08 3.7.08 5.86zm34.4-1.3v-5h9.05v12.05l-.46.17c-2.02.7-4.7 1.2-7.1 1.2-3.9 0-6.8-.97-8.9-2.95-2-1.94-3.1-4.82-3.1-8.03.02-7.05 5.1-11.48 12.48-11.48 2.43 0 4.67.43 6 1.04l.53.24-1.33 5.1-.73-.33c-1.2-.56-2.54-.83-4.5-.83-3.82 0-6.2 2.14-6.2 6 0 3.8 2.3 6.1 5.86 6.1.62 0 1.2-.04 1.6-.1v-3.17h-3.2z"
                ></path>
              </g>
            </svg>
          </div>

          <div className="flex flex-row items-center pb-4">
            <Button
              variant="ghost"
              className="flex w-full items-center text-sm"
              onClick={() => {
                setTheme(theme === 'light' ? 'dark' : 'light');
              }}
            >
              {theme === 'dark' ? (
                <MoonIcon className="mr-2 size-4 text-yellow-200 hover:text-yellow-200" />
              ) : (
                <SunIcon className="mr-2 size-4 text-orange-500 hover:text-orange-500" />
              )}
              <span>{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
            </Button>

            <Button
              variant="ghost"
              onClick={() => logout('/app/#/backoffice/login')}
            >
              <LogOutIcon className="mr-2 size-3.5" />
              <span>Sair</span>
            </Button>
          </div>
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 -z-50 h-52 w-full sm:h-[31rem]">
          <svg
            className="absolute bottom-0 left-0 w-44 fill-portal-primary lg:w-[26rem]"
            viewBox="0 0 374.8 398.7"
            aria-hidden="true"
          >
            <path d="M357.2 354.9c-39-100-80.6-195.4-159.5-264.1C145 45 76.6 14.2 0 .5v82.3c56.3 12.1 106 35.6 144.6 69.1 65 56.5 102 142.2 137.3 232.7 1.8 4.7 3.7 9.4 5.5 14.1h87.3c-6.1-14.4-11.8-29.2-17.5-43.8z"></path>
          </svg>
          <svg
            className="absolute bottom-0 right-0 w-52 fill-portal-primary  lg:w-[31rem]"
            viewBox="0 0 493.47 212"
            aria-hidden="true"
          >
            <path d="M151.83 212c81.32-46.19 171.57-90.97 232.45-102.16-19.15 30.07-60.77 75.78-85.36 102.16h104.59c59.33-69.38 81.8-113.98 61.89-150.74-14.55-26.87-45.62-36.41-92.36-28.32C269.1 50.91 100.43 149.2.58 212h151.25Z"></path>
          </svg>
        </div>
      </footer>
    </div>
  );
}
